/* =============================================================================
 * SEDES Y RAZONES SOCIALES DE CADA TRABAJADOR (11-oct-2026)
 * -----------------------------------------------------------------------------
 * ISITECH tiene gente en distintas plantas y una sola cuenta para dos razones
 * sociales. Hasta ahora la empresa tenía UNA ubicación y UN radio, y quien
 * trabaja en otra planta salía "Fuera (~X km)" en cada checada, impreso en
 * documentos que se firman. Decisiones de Iván:
 *   · UNA sede por trabajador y UNA razón social; se cambian CON FECHA (rotan
 *     gente entre obras) y cada cambio queda en un historial que solo crece
 *     (`historialSede`, `historialRazonSocial` en la ficha; ver firestore.rules);
 *   · las checadas viejas conservan la sede que tenían (`sedeAplicada`, testigo
 *     escrito al checar) y no se reinterpretan;
 *   · checar NUNCA se bloquea por estar fuera: solo se marca.
 *
 * 'principal' es lo de siempre: la ubicación (`ubicacionTrabajo`) y los datos de
 * la empresa de configuracion_empresa/{patrón}. Todo trabajador sin sede ni
 * razón social asignada usa esa, así que nada cambia para quien no configure
 * sedes. Las demás viven en los catálogos `configuracion_empresa/{patrón}/sedes`
 * y `/razones_sociales`: no se borran nunca, se desactivan.
 *
 * CommonJS, exportado nombre por nombre (como src/logica/checador.js).
 * ========================================================================== */

const SEDE_PRINCIPAL = "principal";
const RAZON_PRINCIPAL = "principal";
const RADIOS_SEDE = [100, 200, 300, 500];
// La principal SIN NOMBRE no se llama de ninguna forma: la plataforma no sabe qué es
// ese lugar (12-oct-2026). Sus checadas se leen como siempre ("Dentro de la zona");
// en las pantallas del patrón se señala así, para que le ponga nombre.
const ETIQUETA_PRINCIPAL_SIN_NOMBRE = "Sede principal (sin nombre)";
const AYUDA_NOMBRE_SEDE = "Ponle nombre (por ejemplo, Oficina o Planta Norte)";
// Mientras solo exista la principal puede seguir sin nombre; para dar de alta OTRA
// sede, primero se nombra la principal (12-oct-2026).
const TEXTO_NOMBRA_LA_PRINCIPAL = "Antes de agregar otra sede, ponle nombre a tu sede principal para que las checadas digan de cuál se trata.";
// Y una vez que hay otras sedes, la principal ya no puede quedarse sin nombre.
const TEXTO_PRINCIPAL_NECESITA_NOMBRE = "Tu sede principal necesita nombre mientras tengas otras sedes registradas.";
// EL NOMBRE DEL PATRÓN ESCRITO A MANO en el finiquito (13-oct-2026): ¿es la denominación
// de la razón social que rige al trabajador? No cuentan las mayúsculas, los espacios al
// inicio y al final ni los espacios dobles; los acentos, los puntos y las comas SÍ.
function _normDenominacion(s) { return String(s || "").trim().replace(/\s+/g, " ").toLocaleLowerCase("es-MX"); }
function mismaDenominacion(escrito, denominacion) { return _normDenominacion(escrito) === _normDenominacion(denominacion); }
function textoNombreNoCoincide(nombreTrabajador, denominacion) {
  return "El nombre que escribiste no coincide con la razón social de " + nombreTrabajador + " (" + denominacion + "). El finiquito no incluirá su RFC.";
}
// UNA CUENTA, UNA FICHA (13-oct-2026). Lo que se dice cuando la cuenta ya está en
// otra ficha del patrón (la misma frase en la pestaña Checador y en la tarjeta de sede
// y razón social), y lo que dice el guardado si aun así se intenta.
function textoCuentaEnOtraFicha(otra) {
  return "Su cuenta del portal está vinculada a otra ficha (" + ((otra && (otra.nombre || otra.email)) || "sin nombre") + "), no a esta. En su checador ve el horario de esa ficha. Revisa cuál de las dos es la buena.";
}
const TEXTO_CUENTA_YA_VINCULADA = "Esta cuenta ya está vinculada a otra ficha. Una cuenta solo puede estar vinculada a una ficha.";
// La otra ficha del patrón que ya lleva esa cuenta (cuentaUid), o null.
function otraFichaConLaCuenta(fichas, cuentaUid, fichaId) {
  if (!cuentaUid) return null;
  return (fichas || []).find((f) => f && f.id !== fichaId && String(f.cuentaUid || "") === String(cuentaUid)) || null;
}
const TEXTO_FICHA_SIN_VINCULAR = "Este trabajador no tiene su cuenta vinculada a su ficha. Su teléfono no podrá leer su sede ni su horario propio y medirá contra la sede principal.";
// La principal (Datos de mi empresa) sí deja cambiar el RFC —las reglas no lo fijan—, pero
// si ya había uno guardado se pide confirmación con este texto (12-oct-2026).
const TEXTO_CAMBIO_RFC_PRINCIPAL = "Cambiar el RFC cambia a nombre de quién salen los documentos, incluso los de periodos anteriores si se vuelven a generar. Si es otra empresa, dala de alta como otra razón social.";
// La primera razón social de una ficha: ¿desde cuándo aplica? (12-oct-2026). Sin opción
// preseleccionada. Y lo que se dice antes de guardar un cambio "a partir de hoy".
const OPCION_DESDE_EL_INICIO = "Desde el inicio de la relación laboral (corrige un dato que no estaba capturado)";
const OPCION_A_PARTIR_DE_HOY = "A partir de hoy (cambio de razón social)";
const TEXTO_CAMBIO_DESDE_HOY = "Este cambio aplica desde hoy. Los documentos de cualquier periodo que incluya esta fecha saldrán a nombre de la razón social vigente al cierre del periodo, con una nota del cambio.";
// La primera razón social "desde el inicio" no se puede deshacer (13-oct-2026): la
// línea fija bajo la pregunta y la confirmación, con el nombre y la razón social.
const TEXTO_ELECCION_IRREVERSIBLE = "Esta elección queda registrada y no se puede cambiar después.";
function textoConfirmaDesdeElInicio(nombre, razon) {
  return "Los documentos de " + nombre + " saldrán a nombre de " + razon + " desde el inicio de su relación laboral, incluidos los de periodos anteriores que se vuelvan a generar, con una nota de cuándo y quién lo registró. Esta elección no se puede deshacer: elígela solo si " + nombre + " siempre ha trabajado para " + razon + ".";
}
const TEXTO_RFC_FIJO = "El RFC y el tipo de persona no se pueden cambiar después de guardar: un RFC distinto es otra empresa. Si se capturó mal, da de alta otra razón social con el RFC correcto y desactiva esta.";

function _ms(v) {
  if (typeof v === "number") return v;   // ya en milisegundos (los documentos pasan así el fin del periodo)
  if (!v) return NaN;
  if (typeof v.toMillis === "function") return v.toMillis();
  if (typeof v.getTime === "function") return v.getTime();
  if (typeof v.seconds === "number") return v.seconds * 1000 + Math.floor((Number(v.nanoseconds) || 0) / 1e6);
  return NaN;
}
const _num = (x) => (typeof x === "number" && isFinite(x) ? x : null);

// ── El historial de una asignación: h0, h1, h2… en orden ────────────────────
// Cada entrada: {vigenteDesde, [campo]: valor, por}. Una entrada recién escrita
// cuya hora del servidor aún no llega (pendiente) cuenta como "ahora".
function entradasDeAsignacion(historial, campo) {
  if (!historial || typeof historial !== "object") return [];
  return Object.keys(historial)
    .filter((k) => /^h\d+$/.test(k) && historial[k] && typeof historial[k] === "object")
    .map((k) => ({ n: Number(k.slice(1)), e: historial[k] }))
    .sort((a, b) => a.n - b.n)
    .map((x) => {
      const ms = _ms(x.e.vigenteDesde);
      // EFECTO DESDE EL INICIO (12-oct-2026): solo h0, y solo si lo trae (las reglas lo
      // permiten únicamente en la primera entrada de la razón social).
      return { clave: "h" + x.n, valor: String(x.e[campo] || SEDE_PRINCIPAL), desdeMs: isFinite(ms) ? ms : Date.now(), pendiente: !isFinite(ms), por: String(x.e.por || ""),
        efectoDesdeElInicio: x.n === 0 && x.e.efectoDesdeElInicio === true };
    });
}
// LA CLAVE DE LA SIGUIENTE ENTRADA, contada como la cuentan las reglas: con todas
// las claves del mapa.
// La h0 con efecto desde el inicio de la relación laboral, si la hay: cuándo se
// registró, quién y qué valor. null si no.
function efectoDesdeElInicioDe(ficha, campo, campoHistorial) {
  const es = entradasDeAsignacion(ficha && ficha[campoHistorial], campo);
  return es.length && es[0].efectoDesdeElInicio ? { desdeMs: es[0].desdeMs, por: es[0].por, valor: es[0].valor, pendiente: es[0].pendiente } : null;
}
// ¿Todavía no tiene ninguna entrada? (Lo que las reglas cuentan: el tamaño del mapa.)
function sinHistorialDeAsignacion(historial) { return siguienteClaveDeAsignacion(historial) === "h0"; }
function siguienteClaveDeAsignacion(historial) {
  const n = historial && typeof historial === "object" ? Object.keys(historial).length : 0;
  return "h" + n;
}
// QUÉ VALOR REGÍA en un instante: el de la última entrada con vigenteDesde <= ese
// instante. Antes de h0, o sin historial, 'principal'. Si la ficha trae el valor
// vigente pero no su historial (una ficha escrita antes de las reglas), ese
// valor rige "desde siempre".
function valorQueRegia(ficha, campo, campoHistorial, instante) {
  const t = _ms(instante);
  const es = entradasDeAsignacion(ficha && ficha[campoHistorial], campo);
  if (!es.length) return String((ficha && ficha[campo]) || SEDE_PRINCIPAL);
  // Con efecto desde el inicio, h0 rige también para cualquier instante anterior.
  let v = es[0].efectoDesdeElInicio ? es[0].valor : SEDE_PRINCIPAL;
  for (const e of es) { if (!isFinite(t) || e.desdeMs <= t) v = e.valor; }
  return v;
}
// Los cambios DENTRO de un periodo [desdeMs, hastaMs]: {desdeMs, de, a}. El valor
// "de" es el que regía justo antes.
function cambiosEnElPeriodo(ficha, campo, campoHistorial, desdeMs, hastaMs) {
  const es = entradasDeAsignacion(ficha && ficha[campoHistorial], campo);
  const out = [];
  // Una h0 con efecto desde el inicio no es un cambio: rigió desde siempre.
  let anterior = es.length && es[0].efectoDesdeElInicio ? es[0].valor : SEDE_PRINCIPAL;
  for (const e of es) {
    if (e.desdeMs > desdeMs && e.desdeMs <= hastaMs && e.valor !== anterior) out.push({ desdeMs: e.desdeMs, de: anterior, a: e.valor });
    anterior = e.valor;
  }
  return out;
}

// ── Las sedes ────────────────────────────────────────────────────────────────
// La principal: la ubicación de la empresa, con su radio (se respeta aunque no sea
// 100/200/300/500) y su nombre, vacío si el patrón no se lo ha puesto (no se
// inventa ninguno). null si nunca se fijó.
function sedePrincipalDe(cfg) {
  const ut = cfg && cfg.ubicacionTrabajo;
  const lat = _num(ut && ut.lat), lng = _num(ut && ut.lng);
  const nombre = String((cfg && cfg.nombreSedePrincipal) || "").trim();
  if (lat == null || lng == null) return null;
  return { sedeId: SEDE_PRINCIPAL, nombre, lat, lng, radioMetros: Number(ut.radioMetros) > 0 ? Number(ut.radioMetros) : 200, activa: true, principal: true };
}
// Cómo se nombra una sede en las pantallas del PATRÓN. Las checadas y los documentos
// no la usan: ahí, sin nombre, se dice lo de siempre (ver lecturaUbicacionChecada).
function etiquetaDeSede(s) {
  const n = String((s && s.nombre) || "").trim();
  if (n) return n;
  return s && s.principal ? ETIQUETA_PRINCIPAL_SIN_NOMBRE : "Sede sin nombre";
}
// ¿Hay que nombrar la principal antes de agregar otra sede? Sí, si existe y no tiene nombre.
function faltaNombrarLaPrincipal(sedes) {
  const p = (sedes || []).find((s) => s && s.principal);
  return !!p && !String(p.nombre || "").trim();
}
// ¿Hay alguna sede registrada además de la principal? Activa o desactivada: las
// desactivadas siguen nombradas en historiales y documentos pasados. Se cuentan los
// documentos del catálogo tal cual (`docsSedes`) y, por si no vienen, las sedes armadas.
function hayOtrasSedesRegistradas(sedes, docsSedes) {
  return (docsSedes || []).length > 0 || (sedes || []).some((s) => s && !s.principal);
}
// Una sede del catálogo, en la misma forma. null si no tiene ubicación.
function sedeDelCatalogo(id, d) {
  if (!d) return null;
  const lat = _num(d.lat), lng = _num(d.lng);
  if (lat == null || lng == null) return null;
  return { sedeId: String(id), nombre: String(d.nombre || "").trim() || "Sede", lat, lng, radioMetros: Number(d.radioMetros) > 0 ? Number(d.radioMetros) : 200,
    activa: d.activa !== false, direccion: String(d.direccion || ""), principal: false };
}
// El catálogo completo: la principal primero (si hay ubicación), después las demás.
// `docs`: [{id, ...datos}].
function catalogoDeSedes(cfg, docs) {
  const out = [];
  const p = sedePrincipalDe(cfg);
  if (p) out.push(p);
  (docs || []).forEach((d) => { const s = sedeDelCatalogo(d.id, d); if (s) out.push(s); });
  return out;
}
// LA SEDE CONTRA LA QUE SE MIDE: la asignada, aunque esté desactivada (quien la
// tiene la conserva); si no existe o no tiene ubicación, la principal; si
// tampoco, null (sin geocerca, como hoy).
function sedeDeId(id, catalogo) {
  const lista = catalogo || [];
  return lista.find((s) => s.sedeId === id) || lista.find((s) => s.sedeId === SEDE_PRINCIPAL) || null;
}
function sedeVigenteDe(ficha, catalogo) {
  return sedeDeId(String((ficha && ficha.sedeId) || SEDE_PRINCIPAL), catalogo);
}
function sedeQueRegia(ficha, instante, catalogo) {
  return sedeDeId(valorQueRegia(ficha, "sedeId", "historialSede", instante), catalogo);
}
// EL TESTIGO que va en la checada (forma validada por las reglas: sedeAplicadaValida).
function sedeAplicadaDe(sede) {
  if (!sede || _num(sede.lat) == null || _num(sede.lng) == null || !(Number(sede.radioMetros) > 0) || !sede.sedeId) return null;
  return { sedeId: String(sede.sedeId), nombre: String(sede.nombre || ""), lat: sede.lat, lng: sede.lng, radioMetros: Number(sede.radioMetros) };
}
// Distancia en metros (fórmula del haversine).
function distanciaEntre(lat1, lng1, lat2, lng2) {
  const a1 = _num(lat1), o1 = _num(lng1), a2 = _num(lat2), o2 = _num(lng2);
  if (a1 == null || o1 == null || a2 == null || o2 == null) return null;
  const R = 6371000, rad = (g) => (g * Math.PI) / 180;
  const dLat = rad(a2 - a1), dLng = rad(o2 - o1);
  const h = Math.sin(dLat / 2) ** 2 + Math.cos(rad(a1)) * Math.cos(rad(a2)) * Math.sin(dLng / 2) ** 2;
  return Math.round(2 * R * Math.asin(Math.min(1, Math.sqrt(h))));   // la misma cuenta que distanciaMetros de App.tsx
}
// MEDIR una posición contra una sede. NUNCA bloquea: solo dice dentro o fuera.
// null si no hay posición o no hay sede (la checada se registra igual, sin geocerca).
function medirContraSede(pos, sede) {
  if (!pos || !sede) return null;
  const d = distanciaEntre(pos.lat, pos.lng, sede.lat, sede.lng);
  if (d == null) return null;
  return { distanciaMetros: d, dentroDeUbicacion: d <= Number(sede.radioMetros) };
}
// ¿El testigo de la checada dice OTRA sede que la que regía según el historial?
// Una checada vieja sin testigo no difiere: no se reinterpreta.
function sedeDifiereDelTestigo(checada, ficha, instante) {
  const t = checada && checada.sedeAplicada;
  if (!t || !t.sedeId) return false;
  return String(t.sedeId) !== valorQueRegia(ficha, "sedeId", "historialSede", instante);
}

// ── Las razones sociales ─────────────────────────────────────────────────────
function _domicilio(d) {
  return [d.domCalle, d.domColonia, d.domMunicipio, d.domEstado, d.domCP ? "C.P. " + d.domCP : ""].map((x) => String(x || "").trim()).filter(Boolean).join(", ");
}
// La principal: los Datos de mi empresa de siempre.
function razonSocialPrincipalDe(cfg) {
  const c = cfg || {};
  return { id: RAZON_PRINCIPAL, denominacion: String(c.denominacion || "").trim(), rfc: String(c.rfc || "").trim().toUpperCase(),
    tipoPersona: String(c.tipoPersona || ""), domCalle: String(c.domCalle || ""), domColonia: String(c.domColonia || ""), domMunicipio: String(c.domMunicipio || ""),
    domEstado: String(c.domEstado || ""), domCP: String(c.domCP || ""), domicilio: _domicilio(c), activa: true, principal: true };
}
function razonSocialDelCatalogo(id, d) {
  if (!d) return null;
  return { id: String(id), denominacion: String(d.denominacion || "").trim(), rfc: String(d.rfc || "").trim().toUpperCase(), tipoPersona: String(d.tipoPersona || ""),
    domCalle: String(d.domCalle || ""), domColonia: String(d.domColonia || ""), domMunicipio: String(d.domMunicipio || ""), domEstado: String(d.domEstado || ""),
    domCP: String(d.domCP || ""), domicilio: _domicilio(d), activa: d.activa !== false, principal: false };
}
function catalogoDeRazones(cfg, docs) {
  return [razonSocialPrincipalDe(cfg)].concat((docs || []).map((d) => razonSocialDelCatalogo(d.id, d)).filter(Boolean));
}
// La razón social de un id; si no existe en el catálogo, la principal. Lo que se
// imprime es lo que el catálogo dice HOY de ella: si la denominación se editó
// después del periodo, sale la vigente (no se inventa historial).
function razonSocialDeId(id, catalogo) {
  const lista = catalogo || [];
  return lista.find((r) => r.id === id) || lista.find((r) => r.id === RAZON_PRINCIPAL) || null;
}
function razonSocialVigenteDe(ficha, catalogo) {
  return razonSocialDeId(String((ficha && ficha.razonSocialId) || RAZON_PRINCIPAL), catalogo);
}
function razonSocialQueRegia(ficha, instante, catalogo) {
  return razonSocialDeId(valorQueRegia(ficha, "razonSocialId", "historialRazonSocial", instante), catalogo);
}
// EL TESTIGO de la checada (forma de razonSocialAplicadaValida). null sin denominación.
function razonSocialAplicadaDe(rs) {
  if (!rs || !rs.id || !String(rs.denominacion || "").trim()) return null;
  return { id: String(rs.id), denominacion: String(rs.denominacion).trim() };
}
// RFC CONTRA TIPO DE PERSONA (13-oct-2026): moral → 12 caracteres; física → 13. Se mide
// sin espacios. Con cualquier otra longitud no se dice nada aquí (lo demás sigue igual).
const TEXTO_RFC_Y_TIPO = "El RFC de una persona moral tiene 12 caracteres y el de una persona física, 13. Revisa el RFC o el tipo de persona.";
function rfcContradiceTipo(rfc, tipoPersona) {
  const r = String(rfc || "").replace(/\s+/g, ""), t = String(tipoPersona || "").trim().toLowerCase();
  return (r.length === 12 && t === "fisica") || (r.length === 13 && t === "moral");
}
const RE_RFC = /^[A-ZÑ&]{3,4}[0-9]{6}[A-Z0-9]{3}$/;
function rfcValido(rfc) { return RE_RFC.test(String(rfc || "").trim().toUpperCase()); }
// EL RFC, NORMALIZADO (13-oct-2026): sin espacios y en mayúsculas, antes de validar y de guardar.
function normalizarRfc(rfc) { return String(rfc || "").replace(/\s+/g, "").toUpperCase(); }
// LA VALIDACIÓN DEL RFC, UNA SOLA para el catálogo de razones sociales y Datos de mi
// empresa (13-oct-2026): la forma (3 o 4 letras, 6 dígitos, 3 letras o dígitos) y que
// la longitud corresponda al tipo de persona (moral 12, física 13). null si está bien;
// si no, el texto que se muestra.
const TEXTO_RFC_SIN_FORMA = "El RFC no tiene forma de RFC (12 caracteres si es persona moral, 13 si es física).";
function errorDeRfc(rfc, tipoPersona) {
  const r = normalizarRfc(rfc);
  if (!rfcValido(r)) return TEXTO_RFC_SIN_FORMA;
  if (rfcContradiceTipo(r, tipoPersona)) return TEXTO_RFC_Y_TIPO;
  return null;
}
// ¿Se va a cambiar un RFC que ya estaba guardado? Capturarlo la primera vez no cuenta,
// ni volver a escribir el mismo con otras mayúsculas o espacios.
function rfcCambiaElGuardado(guardado, nuevo) {
  const a = normalizarRfc(guardado), b = normalizarRfc(nuevo);
  return a !== "" && a !== b;
}

// ── ¿Ficha vinculada a su cuenta? ────────────────────────────────────────────
// Sin `cuentaUid`, el teléfono del trabajador no encuentra su ficha (la busca por
// cuentaUid): mide contra la sede principal y con el horario general.
function fichaSinVincular(ficha) { return !String((ficha && ficha.cuentaUid) || "").trim(); }
// Cuántas fichas tienen una cuenta del portal (por correo) pero no están
// vinculadas a ella. `correosConCuenta`: Set de correos en minúsculas.
function fichasConCuentaSinVincular(fichas, correosConCuenta) {
  return (fichas || []).filter((f) => (f.status || "activo") !== "baja" && fichaSinVincular(f)
    && correosConCuenta && correosConCuenta.has(String(f.email || "").toLowerCase().trim()));
}

exports.SEDE_PRINCIPAL = SEDE_PRINCIPAL;
exports.RAZON_PRINCIPAL = RAZON_PRINCIPAL;
exports.RADIOS_SEDE = RADIOS_SEDE;
exports.TEXTO_CAMBIO_RFC_PRINCIPAL = TEXTO_CAMBIO_RFC_PRINCIPAL;
exports.efectoDesdeElInicioDe = efectoDesdeElInicioDe;
exports.sinHistorialDeAsignacion = sinHistorialDeAsignacion;
exports.TEXTO_CAMBIO_DESDE_HOY = TEXTO_CAMBIO_DESDE_HOY;
exports.TEXTO_ELECCION_IRREVERSIBLE = TEXTO_ELECCION_IRREVERSIBLE;
exports.mismaDenominacion = mismaDenominacion;
exports.textoNombreNoCoincide = textoNombreNoCoincide;
exports.textoConfirmaDesdeElInicio = textoConfirmaDesdeElInicio;
exports.textoCuentaEnOtraFicha = textoCuentaEnOtraFicha;
exports.TEXTO_CUENTA_YA_VINCULADA = TEXTO_CUENTA_YA_VINCULADA;
exports.otraFichaConLaCuenta = otraFichaConLaCuenta;
exports.OPCION_DESDE_EL_INICIO = OPCION_DESDE_EL_INICIO;
exports.OPCION_A_PARTIR_DE_HOY = OPCION_A_PARTIR_DE_HOY;
exports.rfcCambiaElGuardado = rfcCambiaElGuardado;
exports.ETIQUETA_PRINCIPAL_SIN_NOMBRE = ETIQUETA_PRINCIPAL_SIN_NOMBRE;
exports.AYUDA_NOMBRE_SEDE = AYUDA_NOMBRE_SEDE;
exports.TEXTO_NOMBRA_LA_PRINCIPAL = TEXTO_NOMBRA_LA_PRINCIPAL;
exports.faltaNombrarLaPrincipal = faltaNombrarLaPrincipal;
exports.TEXTO_PRINCIPAL_NECESITA_NOMBRE = TEXTO_PRINCIPAL_NECESITA_NOMBRE;
exports.hayOtrasSedesRegistradas = hayOtrasSedesRegistradas;
exports.etiquetaDeSede = etiquetaDeSede;
exports.TEXTO_FICHA_SIN_VINCULAR = TEXTO_FICHA_SIN_VINCULAR;
exports.TEXTO_RFC_FIJO = TEXTO_RFC_FIJO;
exports.entradasDeAsignacion = entradasDeAsignacion;
exports.siguienteClaveDeAsignacion = siguienteClaveDeAsignacion;
exports.valorQueRegia = valorQueRegia;
exports.cambiosEnElPeriodo = cambiosEnElPeriodo;
exports.sedePrincipalDe = sedePrincipalDe;
exports.sedeDelCatalogo = sedeDelCatalogo;
exports.catalogoDeSedes = catalogoDeSedes;
exports.sedeDeId = sedeDeId;
exports.sedeVigenteDe = sedeVigenteDe;
exports.sedeQueRegia = sedeQueRegia;
exports.sedeAplicadaDe = sedeAplicadaDe;
exports.distanciaEntre = distanciaEntre;
exports.medirContraSede = medirContraSede;
exports.sedeDifiereDelTestigo = sedeDifiereDelTestigo;
exports.razonSocialPrincipalDe = razonSocialPrincipalDe;
exports.razonSocialDelCatalogo = razonSocialDelCatalogo;
exports.catalogoDeRazones = catalogoDeRazones;
exports.razonSocialDeId = razonSocialDeId;
exports.razonSocialVigenteDe = razonSocialVigenteDe;
exports.razonSocialQueRegia = razonSocialQueRegia;
exports.razonSocialAplicadaDe = razonSocialAplicadaDe;
exports.rfcValido = rfcValido;
exports.normalizarRfc = normalizarRfc;
exports.errorDeRfc = errorDeRfc;
exports.TEXTO_RFC_SIN_FORMA = TEXTO_RFC_SIN_FORMA;
exports.TEXTO_RFC_Y_TIPO = TEXTO_RFC_Y_TIPO;
exports.rfcContradiceTipo = rfcContradiceTipo;
exports.fichaSinVincular = fichaSinVincular;
exports.fichasConCuentaSinVincular = fichasConCuentaSinVincular;
